const SUBSCRIPTION_RETRY_BASE_DELAY_MS = 1000;
const SUBSCRIPTION_RETRY_MAX_DELAY_MS = 30000;
const SUBSCRIPTION_RETRY_JITTER_MS = 1000;

export const SUBSCRIPTION_CONNECTION_STATUS = {
	IDLE: 'idle',
	CONNECTING: 'connecting',
	CONNECTED: 'connected',
	DISCONNECTED: 'disconnected',
	RECONNECTING: 'reconnecting',
};

const getRetryDelay = retries => Math.min(
	SUBSCRIPTION_RETRY_MAX_DELAY_MS,
	SUBSCRIPTION_RETRY_BASE_DELAY_MS * (2 ** Math.min(retries, 10)) +
		Math.floor(Math.random() * SUBSCRIPTION_RETRY_JITTER_MS),
);

export const createSubscriptionRetryController = () => {
	let pendingRetry = null;
	let skipNextWait = false;

	const finishPendingRetry = () => {
		if (!pendingRetry) {
			return;
		}

		clearTimeout(pendingRetry.timeoutId);
		const { resolve } = pendingRetry;
		pendingRetry = null;
		resolve();
	};

	return {
		wait: (retries) => {
			if (skipNextWait) {
				skipNextWait = false;
				return Promise.resolve();
			}

			return new Promise((resolve) => {
				pendingRetry = {
					resolve,
					timeoutId: setTimeout(finishPendingRetry, getRetryDelay(retries)),
				};
			});
		},
		retryNow: () => {
			if (pendingRetry) {
				finishPendingRetry();
				return;
			}

			skipNextWait = true;
		},
	};
};

export const createSubscriptionConnection = () => {
	let reconnect = () => {};
	let state = {
		status: SUBSCRIPTION_CONNECTION_STATUS.IDLE,
		hasConnected: false,
		recoveryCount: 0,
	};
	const listeners = new Set();

	const updateState = (nextState) => {
		state = nextState;
		listeners.forEach(listener => listener(state));
	};

	return {
		getState: () => state,
		subscribe: (listener) => {
			listeners.add(listener);
			return () => listeners.delete(listener);
		},
		setReconnect: (nextReconnect) => {
			reconnect = nextReconnect;
		},
		requestReconnect: () => {
			updateState({
				...state,
				// Opening the app asks for a fresh connection before the first one finished; that is not a reconnect.
				status: state.hasConnected
					? SUBSCRIPTION_CONNECTION_STATUS.RECONNECTING
					: SUBSCRIPTION_CONNECTION_STATUS.CONNECTING,
			});
			reconnect();
		},
		connecting: (isRetry) => {
			updateState({
				...state,
				status: isRetry || state.hasConnected
					? SUBSCRIPTION_CONNECTION_STATUS.RECONNECTING
					: SUBSCRIPTION_CONNECTION_STATUS.CONNECTING,
			});
		},
		connected: (wasRetry) => {
			const didRecover = Boolean(wasRetry || state.hasConnected);

			updateState({
				status: SUBSCRIPTION_CONNECTION_STATUS.CONNECTED,
				hasConnected: true,
				recoveryCount: state.recoveryCount + (didRecover ? 1 : 0),
			});

			return didRecover;
		},
		disconnected: () => {
			if (state.status === SUBSCRIPTION_CONNECTION_STATUS.DISCONNECTED) {
				return;
			}

			updateState({
				...state,
				status: SUBSCRIPTION_CONNECTION_STATUS.DISCONNECTED,
			});
		},
	};
};
